import React, { useState } from 'react';
import { useApiQuery } from '../../hooks/useApiQuery';
import AdminLayout from './AdminDashboardLayout';
import { 
  Shield, 
  User, 
  Users, 
  Settings, 
  LogIn, 
  LogOut, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Calendar,
  Globe,
  Smartphone
} from 'lucide-react';
import { format } from 'date-fns';

interface AuditLog {
  id: number;
  actor: {
    id: number;
    email: string;
    fullName: string;
    role: string;
    partner?: {
      id: number;
      name: string;
      partnerIdentifier: string;
    };
  } | null;
  partner: {
    id: number;
    name: string;
    partnerIdentifier: string;
  } | null;
  eventType: string;
  action: string;
  metadata: Record<string, any>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  content: AuditLog[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

const AuditLogs: React.FC = () => {
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('');

  const { data, isLoading, error } = useApiQuery<any>({
    queryKey: ['audit-logs', page, searchTerm, selectedEventType],
    request: {
      url: '/audit-logs',
      method: 'GET',
      params: {
        page,
        size: 10,
        search: searchTerm,
        eventType: selectedEventType
      }
    }
  });


  const auditData = data?.data;

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN':
        return <LogIn className="w-4 h-4 text-green-600" />;
      case 'LOGOUT':
        return <LogOut className="w-4 h-4 text-orange-600" />;
      case 'PARTNER_CREATED':
        return <Users className="w-4 h-4 text-blue-600" />;
      case 'PARTNER_UPDATED':
        return <Settings className="w-4 h-4 text-purple-600" />;
      case 'ACCESS_DENIED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Shield className="w-4 h-4 text-gray-600" />;
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'LOGOUT':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'PARTNER_CREATED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PARTNER_UPDATED':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ACCESS_DENIED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatMetadata = (metadata: Record<string, any>) => {
    if (!metadata || Object.keys(metadata).length === 0) return '-';
    
    try {
      return JSON.stringify(metadata, null, 0);
    } catch {
      return 'Complex metadata';
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  if (isLoading) {
    return (
      <AdminLayout role="SYSTEM ADMIN">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout role="SYSTEM ADMIN">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">Failed to load audit logs. Please try again.</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout role="SYSTEM ADMIN">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-600 mt-1">Monitor system activities and security events</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-2 text-sm text-gray-600">
            <Shield className="w-4 h-4" />
            <span>Secure Logging Enabled</span>
          </div>
        </div>

       

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditData?.content?.map((log: AuditLog) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        {getEventIcon(log.eventType)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getEventColor(log.eventType)}`}>
                          {log.eventType.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        {log.actor ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {log.actor.fullName}
                            </div>
                            <div className="text-gray-500">{log.actor.email}</div>
                            <div className="text-xs text-gray-400">{log.actor.role.replace('_', ' ')}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">System</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        {log.partner ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {log.partner.name}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {log.partner.partnerIdentifier}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 mb-1">
                          {log.action}
                        </div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {formatMetadata(log.metadata)}
                        </div>
                        {(log.metadata?.ipAddress || log.metadata?.location) && (
                          <div className="flex items-center space-x-2 mt-1">
                            {log.metadata?.ipAddress && (
                              <div className="flex items-center text-xs text-gray-400">
                                <Globe className="w-3 h-3 mr-1" />
                                {log.metadata.ipAddress}
                              </div>
                            )}
                            {log.metadata?.location && (
                              <div className="flex items-center text-xs text-gray-400">
                                <span className="ml-2">{log.metadata.location}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm')}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {auditData && auditData.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {auditData.pageNumber * auditData.pageSize + 1} to{' '}
                {Math.min((auditData.pageNumber + 1) * auditData.pageSize, auditData.totalElements)} of{' '}
                {auditData.totalElements} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={auditData.first}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {auditData.pageNumber + 1} of {auditData.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={auditData.last}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {auditData?.content?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
            <p className="text-gray-600">
              {searchTerm || selectedEventType
                ? 'Try adjusting your filters to see more results.'
                : 'Audit logs will appear here as system events occur.'}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AuditLogs;
